import type { MeResponse } from "@safeplate/contracts";

import { requestApi } from "../api/client.js";
import { ApiError } from "../api/errors.js";

export interface SharedDeviceProfile {
  display_name: string;
  image: string | null;
  roles: Array<"admin" | "manager" | "staff">;
  staff_profile_id: string;
}

export interface SharedDeviceRoster {
  device_id: string;
  org_id: string;
  org_name: string;
  profiles: SharedDeviceProfile[];
}

let volatileInstallationId: string | null = null;

export function browserInstallationId(): string {
  const key = "safeplate_browser_installation";
  try {
    const stored = globalThis.localStorage.getItem(key);
    if (stored) return stored;
    const created = crypto.randomUUID();
    globalThis.localStorage.setItem(key, created);
    return created;
  } catch {
    volatileInstallationId ??= crypto.randomUUID();
    return volatileInstallationId;
  }
}

export function bindSharedDevice(orgId: string): Promise<void> {
  return requestApi("/shared-devices", {
    body: JSON.stringify({
      org_id: orgId,
      installation_id: browserInstallationId(),
      platform: "web",
      display_name: "Web browser",
    }),
    headers: { "X-Org-Id": orgId },
    method: "POST",
  });
}

// null: this browser isn't set up as a shared computer yet. The server says
// so with 200 {"bound": false} (RDY-BE-41); older servers answer 401, which
// callers still treat the same way.
export async function getSharedDeviceRoster(): Promise<SharedDeviceRoster | null> {
  const roster = await requestApi<
    SharedDeviceRoster | { bound: false; profiles: [] }
  >("/shared-device/roster");
  return "bound" in roster && roster.bound === false
    ? null
    : (roster as SharedDeviceRoster);
}

// Not set up yet, by either answer.
export function isUnboundRoster(data: unknown, error: unknown): boolean {
  return data === null || (error instanceof ApiError && error.status === 401);
}

export function enrollCurrentDeviceAccount(): Promise<void> {
  return requestApi("/shared-device/accounts", { method: "POST" });
}

export function removeCurrentDeviceAccount(): Promise<void> {
  return requestApi("/shared-device/accounts/me", { method: "DELETE" });
}

export type PinUnlock =
  { kind: "choose"; staffProfileIds: string[] } | { kind: "unlocked" };

// 409 pin_ambiguous: the PIN belongs to more than one person on this
// computer. The body lists only them.
export function ambiguousPinMatches(error: unknown): string[] | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const detail = (
    error.body as { error?: { code?: unknown; matches?: unknown } }
  )?.error;
  if (detail?.code !== "pin_ambiguous" || !Array.isArray(detail.matches))
    return null;
  return detail.matches
    .map((match: { staff_profile_id?: unknown }) => match?.staff_profile_id)
    .filter((id): id is string => typeof id === "string");
}

// Without a person, the server finds who the PIN belongs to among the people
// on this computer.
export async function unlockSharedDevice(
  pin: string,
  staffProfileId?: string,
): Promise<PinUnlock> {
  try {
    await requestApi("/shared-device/unlock", {
      body: JSON.stringify(
        staffProfileId ? { pin, staff_profile_id: staffProfileId } : { pin },
      ),
      method: "POST",
    });
    return { kind: "unlocked" };
  } catch (error) {
    const matches = ambiguousPinMatches(error);
    if (matches?.length) return { kind: "choose", staffProfileIds: matches };
    throw error;
  }
}

export interface DevicePersonResult {
  display_name: string;
  quick_pin_enabled: boolean;
  result: "added" | "already_added" | "no_login" | "unavailable";
  staff_profile_id: string;
}

// A manager signed in with a password puts everyone assigned to the
// location who has a login on this computer, without their passwords.
export function addEveryoneToDevice(
  locationId: string,
): Promise<{ people: DevicePersonResult[] }> {
  return requestApi<{ people: DevicePersonResult[] }>("/shared-device/people", {
    body: JSON.stringify({ location_id: locationId }),
    method: "POST",
  });
}

// "3 added · 1 without a login"
export function addEveryoneSummary(
  people: readonly DevicePersonResult[],
): string {
  const count = (result: DevicePersonResult["result"]) =>
    people.filter((person) => person.result === result).length;
  const added = count("added");
  const noLogin = count("no_login");
  return [
    added === 0 ? "Everyone is already here" : `${added} added`,
    noLogin > 0 ? `${noLogin} without a login` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function lockSharedDevice(): Promise<void> {
  return requestApi("/shared-device/lock", { method: "POST" });
}

export function unbindSharedDevice(): Promise<void> {
  return requestApi("/shared-device", { method: "DELETE" });
}

export function setStaffQuickPin(
  staffProfileId: string,
  pin: string,
): Promise<void> {
  return requestApi(`/staff/${staffProfileId}/quick-pin`, {
    body: JSON.stringify({ pin }),
    method: "PUT",
  });
}

export function scopeMeToOrganisation(
  me: MeResponse,
  orgId: string,
): MeResponse {
  return {
    ...me,
    memberships: me.memberships.filter(
      (membership) => membership.org_id === orgId,
    ),
    staff_profiles: me.staff_profiles.filter(
      (profile) => profile.org_id === orgId,
    ),
  };
}
